/**
 * MoveProjectDialog — 프로젝트를 다른 워크스페이스로 이동.
 * 대상 목록은 서버의 워크스페이스 행 전부(기본 행이 먼저 — 순서·판별은 app/workspaceFilter 하나).
 */

import React, { useEffect, useState } from 'react';
import { Button, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { Dialog } from '@/shared/ui/overlays';
import {
  Inbox as DefaultIcon,
  Workspaces as WorkspaceIcon,
} from '@mui/icons-material';
import { orderWorkspaces } from '@/app/workspaceFilter';
import type { ProjectSummary, Workspace } from '@/types/project';

interface MoveProjectDialogProps {
  project: ProjectSummary | null; // null이면 닫힘
  workspaces: Workspace[];
  onClose: () => void;
  /** 대상 워크스페이스 id — 기본 행도 다른 행과 같이 id 로 */
  onMove: (projectId: string, workspaceId: string) => void;
}

const MoveProjectDialog: React.FC<MoveProjectDialogProps> = ({
  project,
  workspaces,
  onClose,
  onMove,
}) => {
  const [target, setTarget] = useState<string>('');

  // 다이얼로그가 열릴 때 현재 소속을 초기 선택으로
  useEffect(() => {
    if (project) setTarget(project.workspace_id);
  }, [project]);

  const unchanged = target === project?.workspace_id;

  return (
    <Dialog open={Boolean(project)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>"{project?.name}" 워크스페이스로 이동</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <List dense>
          {orderWorkspaces(workspaces, 'created').map((ws) => (
            <ListItemButton
              key={ws.id}
              selected={target === ws.id}
              onClick={() => setTarget(ws.id)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {ws.is_default ? <DefaultIcon fontSize="small" /> : <WorkspaceIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={ws.name} secondary={ws.description || undefined} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button
          variant="contained"
          disabled={unchanged || !project}
          onClick={() => project && onMove(project.id, target)}
        >
          이동
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveProjectDialog;
